import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { SmsActivity } from "@/components/SmsActivity";
import { SmsDigestSettings } from "@/components/SmsDigestSettings";
import { SmsTestButton } from "@/components/SmsTestButton";
import { AutomaticTexts } from "@/components/AutomaticTexts";
import { ClinicPhoneSetting } from "@/components/ClinicPhoneSetting";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Ban, Check, Inbox, Link2, ShieldOff, Clock, Save, Plus, UserPlus, Trash2, PauseCircle, PlayCircle } from "lucide-react";

export default function Messaging() {
  const qc = useQueryClient();
  const [newPhone, setNewPhone] = useState("");
  const [linkSel, setLinkSel] = useState({});
  const [newName, setNewName] = useState({});
  const [settingsForm, setSettingsForm] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const { data: optouts = [] } = useQuery({ queryKey: ["optouts"], queryFn: async () => (await api.get("/sms/optouts")).data });
  const { data: unmatched = [] } = useQuery({ queryKey: ["unmatched"], queryFn: async () => (await api.get("/sms/unmatched")).data });
  const { data: patients = [] } = useQuery({ queryKey: ["patients"], queryFn: async () => (await api.get("/patients")).data });
  const { data: settings } = useQuery({ queryKey: ["sms-settings"], queryFn: async () => (await api.get("/settings/sms")).data });
  const { data: pause } = useQuery({ queryKey: ["sms-pause"], queryFn: async () => (await api.get("/sms/pause")).data });
  useEffect(() => { if (settings && !settingsForm) setSettingsForm(settings); }, [settings, settingsForm]);

  const togglePause = useMutation({
    mutationFn: async (paused) => (await api.post("/sms/pause", { paused })).data,
    onSuccess: (d) => {
      toast.success(d.paused ? "All texts paused" : "Texts resumed and sending normally");
      qc.invalidateQueries({ queryKey: ["sms-pause"] });
      qc.invalidateQueries({ queryKey: ["sms-status"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"),
  });

  const setOpt = useMutation({
    mutationFn: async ({ phone, opted_out }) => (await api.post("/sms/optouts", { phone, opted_out })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["optouts"] }),
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"),
  });
  const addOpt = () => {
    if (!newPhone.trim()) return;
    setOpt.mutate({ phone: newPhone, opted_out: true });
    setNewPhone("");
    toast.success("Number opted out");
  };

  const link = useMutation({
    mutationFn: async ({ from_phone, patient_id }) => (await api.post("/messages/link", { from_phone, patient_id })).data,
    onSuccess: (d) => {
      toast.success(`Linked ${d.linked} message(s) to ${d.patient_name}`);
      qc.invalidateQueries({ queryKey: ["unmatched"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const linkNew = useMutation({
    mutationFn: async ({ from_phone, name, kind, service_interest }) => (await api.post("/messages/link-new", { from_phone, name, kind, service_interest })).data,
    onSuccess: (d) => {
      toast.success(`Created new ${d.created}`);
      qc.invalidateQueries({ queryKey: ["unmatched"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["patients"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"),
  });

  const saveSettings = useMutation({
    mutationFn: async () => (await api.post("/settings/sms", settingsForm)).data,
    onSuccess: () => { toast.success("SMS settings saved"); qc.invalidateQueries({ queryKey: ["sms-settings"] }); qc.invalidateQueries({ queryKey: ["automatic-texts"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"),
  });
  const quietBad = settingsForm && !(Number(settingsForm.quiet_start) >= 0 && Number(settingsForm.quiet_start) < Number(settingsForm.quiet_end) && Number(settingsForm.quiet_end) <= 24);

  const groups = Object.values(unmatched.reduce((acc, m) => {    (acc[m.from_phone] = acc[m.from_phone] || { from_phone: m.from_phone, msgs: [] }).msgs.push(m);
    return acc;
  }, {}));

  return (
    <Layout title="SMS Inbox & Compliance" subtitle="Manage opt-outs and link unknown texters to patients">
      <div data-testid="sms-pause-banner"
        className={`mb-6 rounded-xl border p-4 flex flex-wrap items-center justify-between gap-4 ${pause?.paused ? "bg-red-50 border-red-200" : "bg-white border-slate-200 shadow-sm"}`}>
        <div className="flex items-center gap-3">
          <span className={`h-10 w-10 rounded-lg flex items-center justify-center ${pause?.paused ? "bg-red-100 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>
            {pause?.paused ? <PauseCircle className="h-5 w-5" /> : <PlayCircle className="h-5 w-5" />}
          </span>
          <div>
            <div className="font-heading font-bold text-slate-900">
              {pause?.paused ? "All outgoing SMS are PAUSED" : "Outgoing SMS are active"}
            </div>
            <div className="text-xs text-slate-500">
              {pause?.paused
                ? "No texts — manual, reminders, automations or replies — will be sent until you resume."
                : "Reminders, replies and automations send normally."}
              {pause?.updated_at ? ` · Last changed ${fmtDateTime(pause.updated_at)}${pause.updated_by ? " by " + pause.updated_by : ""}` : ""}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 shrink-0">
          <SmsTestButton />
          <AutomaticTexts />
          {pause?.paused ? (
            <Button data-testid="sms-resume-button" size="sm" disabled={togglePause.isPending} onClick={() => setConfirm("resume")}
              className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white"><PlayCircle className="h-4 w-4 mr-1.5" />Resume texts</Button>
          ) : (
            <Button data-testid="sms-pause-button" size="sm" disabled={togglePause.isPending} onClick={() => setConfirm("pause")}
              className="h-9 bg-red-600 hover:bg-red-700 text-white"><PauseCircle className="h-4 w-4 mr-1.5" />Pause all texts</Button>
          )}
        </div>
        <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
          <AlertDialogContent data-testid="sms-pause-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>{confirm === "resume" ? "Resume all texts?" : "Pause all texts?"}</AlertDialogTitle>
              <AlertDialogDescription>
                {confirm === "resume"
                  ? "Texts will start sending again right away: replies, appointment reminders, no-show follow-ups, recall campaigns and other automations."
                  : "No texts will be sent (manual messages, reminders, follow-ups, recall or automations) until someone clicks Resume texts."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="sms-pause-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="sms-pause-confirm-ok" onClick={() => togglePause.mutate(confirm === "pause")}
                className={confirm === "resume" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}>
                {confirm === "resume" ? "Yes, resume texts" : "Yes, pause all texts"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <SmsActivity />
      <SmsDigestSettings />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Opt-outs */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <ShieldOff className="h-4 w-4 text-red-500" />
            <h3 className="font-heading font-bold text-slate-900">Opt-Out List ({optouts.filter((o) => o.opted_out).length})</h3>
          </div>
          <div className="p-4 flex gap-2 border-b border-slate-50">
            <Input data-testid="optout-phone-input" value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
              placeholder="Add number to opt out (e.g. +1346…)" className="h-10" />
            <Button data-testid="optout-add-button" onClick={addOpt} disabled={setOpt.isPending}
              className="bg-slate-900 hover:bg-slate-800 text-white h-10 shrink-0"><Ban className="h-4 w-4 mr-1.5" />Opt out</Button>
          </div>
          <div className="divide-y divide-slate-50 max-h-[460px] overflow-y-auto">
            {optouts.length === 0 && <div className="px-6 py-6 text-sm text-slate-400">No opt-outs recorded.</div>}
            {optouts.map((o) => (
              <div key={o.id} data-testid={`optout-row-${o.id}`} className="px-6 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono-metric text-sm text-slate-800">{o.phone}</div>
                  <div className="text-[11px] text-slate-400">{o.opted_out ? "Opted out" : "Subscribed"} · {o.updated_at ? fmtDateTime(o.updated_at) : ""}</div>
                </div>
                {o.opted_out ? (
                  <Button data-testid={`resub-${o.id}`} size="sm" variant="outline" className="shrink-0"
                    onClick={() => setOpt.mutate({ phone: o.phone, opted_out: false })}>
                    <Check className="h-3.5 w-3.5 mr-1.5" />Re-subscribe
                  </Button>
                ) : (
                  <span className="text-xs font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">Subscribed</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Unknown inbox */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <Inbox className="h-4 w-4 text-cyan-500" />
            <h3 className="font-heading font-bold text-slate-900">Unknown-Number Inbox ({groups.length})</h3>
          </div>
          <div className="divide-y divide-slate-50 max-h-[520px] overflow-y-auto">
            {groups.length === 0 && (
              <div className="px-6 py-6 text-sm text-slate-400">No unmatched messages. Texts from numbers not tied to a patient appear here.</div>
            )}
            {groups.map((g) => (
              <motion.div key={g.from_phone} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                data-testid={`unmatched-${g.from_phone}`} className="px-6 py-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono-metric text-sm font-semibold text-slate-800">{g.from_phone}</span>
                  <span className="text-[11px] text-slate-400">{g.msgs.length} msg(s)</span>
                </div>
                <div className="space-y-1.5 mb-3">
                  {g.msgs.slice(0, 3).map((m, i) => (
                    <div key={i} className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-1.5">{m.body}</div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Select value={linkSel[g.from_phone] || ""} onValueChange={(v) => setLinkSel({ ...linkSel, [g.from_phone]: v })}>
                    <SelectTrigger data-testid={`link-select-${g.from_phone}`} className="h-9 flex-1"><SelectValue placeholder="Select patient…" /></SelectTrigger>
                    <SelectContent>
                      {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button data-testid={`link-btn-${g.from_phone}`} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white h-9 shrink-0"
                    disabled={!linkSel[g.from_phone] || link.isPending}
                    onClick={() => link.mutate({ from_phone: g.from_phone, patient_id: linkSel[g.from_phone] })}>
                    <Link2 className="h-3.5 w-3.5 mr-1.5" />Link
                  </Button>
                </div>
                <div className="flex gap-2 mt-2">
                  <Input data-testid={`newname-${g.from_phone}`} value={newName[g.from_phone] || ""}
                    onChange={(e) => setNewName({ ...newName, [g.from_phone]: e.target.value })}
                    placeholder="New contact name" className="h-9 flex-1" />
                  <Button data-testid={`newlead-${g.from_phone}`} size="sm" variant="outline" className="h-9 shrink-0"
                    disabled={!newName[g.from_phone] || linkNew.isPending}
                    onClick={() => linkNew.mutate({ from_phone: g.from_phone, name: newName[g.from_phone], kind: "lead" })}>
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" />New lead
                  </Button>
                  <Button data-testid={`newpatient-${g.from_phone}`} size="sm" variant="outline" className="h-9 shrink-0"
                    disabled={!newName[g.from_phone] || linkNew.isPending}
                    onClick={() => linkNew.mutate({ from_phone: g.from_phone, name: newName[g.from_phone], kind: "patient" })}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" />New patient
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {settingsForm && (
        <div data-testid="sms-settings-card" className="bg-white border border-slate-200 rounded-xl shadow-sm mt-6 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <Clock className="h-4 w-4 text-cyan-500" />
            <h3 className="font-heading font-bold text-slate-900">SMS Automation Settings</h3>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <div className="text-sm font-semibold text-slate-700 mb-2">Quiet hours — automated texts only send inside this window</div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                <span className="flex items-center gap-2">Send from
                  <Input type="number" min="0" max="23" data-testid="quiet-start" className="h-9 w-20"
                    value={settingsForm.quiet_start} onChange={(e) => setSettingsForm({ ...settingsForm, quiet_start: Number(e.target.value) })} />:00
                </span>
                <span className="flex items-center gap-2">to
                  <Input type="number" min="0" max="24" data-testid="quiet-end" className="h-9 w-20"
                    value={settingsForm.quiet_end} onChange={(e) => setSettingsForm({ ...settingsForm, quiet_end: Number(e.target.value) })} />:00
                </span>
                <span className="flex items-center gap-2">UTC offset
                  <Input type="number" data-testid="tz-offset" className="h-9 w-20"
                    value={settingsForm.tz_offset} onChange={(e) => setSettingsForm({ ...settingsForm, tz_offset: Number(e.target.value) })} />
                </span>
              </div>
              {quietBad && <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="quiet-hours-warning">
                <span>These quiet hours are backwards (from {settingsForm.quiet_start}:00 to {settingsForm.quiet_end}:00), so automatic texts will never send. The start hour must be earlier than the end hour.</span>
                <Button size="sm" variant="outline" className="h-7 border-red-300 bg-white" onClick={() => setSettingsForm({ ...settingsForm, quiet_start: 8, quiet_end: 21 })} data-testid="quiet-hours-fix">Use 8:00 to 21:00</Button>
              </div>}
            </div>
            <ClinicPhoneSetting />
            <div>
              <div className="text-sm font-semibold text-slate-700 mb-2">Online booking link (used for {"{{booking_link}}"} in replies)</div>
              <Input data-testid="booking-url" value={settingsForm.booking_url || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, booking_url: e.target.value })}
                placeholder="https://your-booking-url" className="h-9 max-w-md" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-700">Keyword auto-replies</div>
                <Button size="sm" variant="outline" data-testid="add-autoreply"
                  onClick={() => setSettingsForm({ ...settingsForm, auto_replies: [...(settingsForm.auto_replies || []), { keyword: "", reply: "" }] })}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" />Add
                </Button>
              </div>
              <div className="space-y-2">
                {(settingsForm.auto_replies || []).map((r, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <Input data-testid={`ar-keyword-${i}`} className="h-9 w-32" placeholder="KEYWORD" value={r.keyword}
                      onChange={(e) => { const a = [...settingsForm.auto_replies]; a[i] = { ...a[i], keyword: e.target.value }; setSettingsForm({ ...settingsForm, auto_replies: a }); }} />
                    <Input data-testid={`ar-reply-${i}`} className="h-9 flex-1" placeholder="Auto-reply message" value={r.reply}
                      onChange={(e) => { const a = [...settingsForm.auto_replies]; a[i] = { ...a[i], reply: e.target.value }; setSettingsForm({ ...settingsForm, auto_replies: a }); }} />
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-red-600 shrink-0" data-testid={`ar-del-${i}`}
                      onClick={() => setSettingsForm({ ...settingsForm, auto_replies: settingsForm.auto_replies.filter((_, j) => j !== i) })}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <Button data-testid="save-sms-settings" onClick={() => saveSettings.mutate()} disabled={saveSettings.isPending || quietBad}
              className="bg-slate-900 hover:bg-slate-800 text-white">
              <Save className="h-4 w-4 mr-2" />Save settings
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}
